import { Avatar, Checkbox, Dropdown, Popover, Spin, Tag, Tooltip } from 'antd';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    LinkOutlined,
    MoreOutlined,
    CalendarOutlined,
    PushpinOutlined,
    PlusSquareOutlined,
    EditOutlined,
    CopyOutlined,
    DeleteOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    LoadingOutlined,
    CheckCircleTwoTone,
    ExclamationCircleOutlined,
    SyncOutlined,
    EllipsisOutlined,
    InfoCircleOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import KpiColumnFilterPopover, {
    KpiFilterValue,
} from 'components/molecules/TradePromoOptimization/KpiColumnFilterPopover';
import DownloadFileButton from 'components/molecules/common/DownloadFileButton';
import { tradePromoOptimizationUrls } from 'config/urls';
import { BulkExportReportStatus, PENDING_BULK_EXPORT_STATUSES, TPO_UI_LIMITS, TpoMessages } from 'constants/tpo';
import { StyledEllipsisButton } from 'components/molecules/TradePromoOptimization/tpoStyledComponents';
import { colorConstants } from 'config/colorConstants';
import CalenderEventItem from 'components/molecules/CalenderEventItem';
import MinusSvgComponent from 'assets/svg/Minus';
import { graphXAxisLabelFontSize } from 'components/organism/GrowthAnalysis/SalesAttributionDrilldownChart';
import { renderTitle } from './getSalesAttributionColumns';
import { fetchCurrency, formatNumWithKMBAlt, formatNumberWithCommas } from './helperMethods';
import { eventStatusType, SelectedPlanInfoType, PromoConfigDictionary } from 'types/tradePromoOptimization';
import _ from 'lodash';
import { getOptimizationStatusIcon } from './optimizationStatusUtils';
import { PlanStatus } from 'constants/tpo';
import { isPostAuditOrWriteOffPromo } from './helperMethods';
import dayjs from 'dayjs';

const antIcon = <LoadingOutlined style={{ fontSize: 18, color: 'blue' }} spin />;

const PlanRowActionsPopover = ({
    rowData,
    editClickHandler,
    deleteClickHandler,
}: {
    rowData: any;
    editClickHandler: Function;
    deleteClickHandler: Function;
}) => {
    const [open, setOpen] = useState(false);
    const isDraft = rowData?.draft_plan_version.toLowerCase() === PlanStatus.DRAFT;

    return (
        <Popover
            open={open}
            onOpenChange={setOpen}
            trigger="click"
            showArrow={false}
            placement="bottomRight"
            overlayInnerStyle={{ borderRadius: 6, padding: '4px 0', minWidth: 150 }}
            content={
                <div>
                    <div
                        onClick={() => {
                            setOpen(false);
                            editClickHandler(rowData);
                        }}
                        className="flex items-center gap-2 cursor-pointer px-3 py-2 hover:bg-gray-100"
                    >
                        <EditOutlined />
                        <span className="font-xs">Edit</span>
                    </div>
                    <Tooltip
                        title={isDraft ? '' : 'Only plans in Draft status can be deleted'}
                        placement="left"
                        destroyTooltipOnHide
                        overlayInnerStyle={{ fontSize: 11 }}
                    >
                        <div
                            onClick={() => {
                                if (!isDraft) return;
                                setOpen(false);
                                deleteClickHandler(rowData);
                            }}
                            className={`flex items-center gap-2 px-3 py-2 ${
                                isDraft
                                    ? 'cursor-pointer hover:bg-gray-100 text-redAlt'
                                    : 'cursor-not-allowed text-gray-300'
                            }`}
                        >
                            <DeleteOutlined style={{ position: 'relative', top: -1 }} />
                            <span className="font-xs">Delete</span>
                        </div>
                    </Tooltip>
                </div>
            }
        >
            <span onClick={(e) => e.stopPropagation()} className="p-2 cursor-pointer flex items-center justify-center">
                <MoreOutlined style={{ fontSize: 16 }} />
            </span>
        </Popover>
    );
};

export const tpoPlansTableColumns = (
    indexTableFixedColumns: any[],
    attributeColumns: any[] = [],
    statuses: any,
    pathname: string,
    sortObj: { sortBy: any; sortOrder: any },
    editClickHandler: Function,
    showSyncedPlans: boolean,
    deleteClickHandler: Function = () => {},
) => {
    let result: any[] = [];

    const mergedHeaders = [
        ...indexTableFixedColumns.slice(0, 2),
        ...attributeColumns, //attributeColumns
        ...indexTableFixedColumns.slice(2),
    ];

    //1st column will be status icon column
    // result.push({
    //     title: '',
    //     dataIndex: 'status_icon',
    //     key: 'status_icon',
    //     width: 30,
    //     onCell: (record: any, rowIndex: number) => {
    //         return {
    //             style: {
    //                 padding: '0px 0px 0px 6px',
    //             },
    //         };
    //     },
    //     render: (columnData: any, rowData: any) => {
    //         return <>{columnData}</>;
    //     },
    // });

    //rest of columns
    const tempColumns = mergedHeaders?.map((header: any, index: number) => {
        return {
            title: <p className={`text-[14px] text-purpleFont`}>{header?.label}</p>,
            dataIndex: header?.key,
            key: header?.key,
            onCell: (record: any, rowIndex: number) => {
                if (header?.key === 'edit')
                    return {
                        style: {
                            padding: 0,
                        },
                    };
            },
            fixed:
                header?.key === 'plan_name' || header?.key === 'last_optimised_status'
                    ? 'left'
                    : header?.key === 'edit'
                    ? 'right'
                    : undefined,
            sortKey: header?.sortKey, //adding a sort key so that on sort click we can read value from this key
            sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: header?.sortKey ? true : false,
            sortDirections: header?.key === 'updated_at' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            width:
                header?.key === 'last_optimised_status'
                    ? 40
                    : [0, 1].includes(index)
                    ? 210
                    : header?.key === 'edit'
                    ? 40
                    : header?.key === 'status'
                    ? 100
                    : 140,
            render: (columnData: any, rowData: any) => {
                const {
                    avatar_short_name,
                    last_optimised_status,
                    created_by,
                    status,
                    is_tpo_synced,
                    is_view_mode,
                    plan_id,
                    key,
                } = rowData;

                //for optimization status column
                if (header?.key === 'last_optimised_status') {
                    return getOptimizationStatusIcon(last_optimised_status);
                }

                //for created by column
                if (header?.key === 'created_by') {
                    return avatar_short_name ? (
                        <div className="flex items-center gap-2">
                            <Avatar
                                className="!text-purpleFont !bg-bgBackground min-w-[32px]"
                                children={<span className="font-xs">{avatar_short_name}</span>}
                            />
                            <p className="text-blackAlt break-all">{created_by}</p>
                        </div>
                    ) : null;
                }

                //for status column
                if (header?.key === 'status') {
                    const itemStatus = statuses?.[showSyncedPlans ? rowData?.approval_status : columnData];
                    return (
                        <Tag style={{ borderRadius: 4 }} color={itemStatus?.colorType || 'default'}>
                            {itemStatus?.label}
                        </Tag>
                    );
                }

                //plan name column
                if (header?.key === 'plan_name') {
                    const url = `${pathname}/view/${plan_id}`;
                    //   const url = `${pathname}/${is_view_mode ? 'view' : 'update'}/${plan_id}`;
                    return (
                        <div style={{ wordBreak: 'break-all' }} className="flex gap-2 items-center">
                            {/* <span className="mr-2 ">
                                <Checkbox disabled={status === 'expired'} />
                            </span> */}
                            <Link className="text-blackAlt underline" to={url}>
                                {columnData}
                            </Link>
                            {is_tpo_synced ? (
                                <span>
                                    <LinkOutlined
                                        style={{
                                            position: 'relative',
                                            top: -2,
                                            fontSize: 18,
                                            color: colorConstants.violet,
                                        }}
                                    />
                                </span>
                            ) : null}
                        </div>
                    );
                }
                //actions menu on plan row
                if (header?.key === 'edit' && !is_tpo_synced) {
                    return (
                        <PlanRowActionsPopover
                            rowData={rowData}
                            editClickHandler={editClickHandler}
                            deleteClickHandler={deleteClickHandler}
                        />
                    );
                }
                //rest of columns
                return <div className={`text-blackAlt ${index === 0 ? 'font-medium' : ''}`}>{columnData}</div>;
            },
        };
    });
    result.push(...tempColumns);
    return result;
};

export const statusIconRenderer = (isTpmTab: any, item: any, rowVal: any, isPublishing: null | string) => {
    const isDeletedRecord = rowVal?.operation?.type?.toLowerCase() === 'deleted';
    if (
        isPublishing === 'INPROGRESS' &&
        rowVal?.promoStatus?.toLowerCase() === 'planned' &&
        rowVal.is_publishable &&
        !isDeletedRecord
    ) {
        return <SyncOutlined style={{ color: '#44338A', fontSize: 16 }} spin />;
    }
    const status = rowVal.publish_status;
    const timeRaw = rowVal.publish_time;
    const time = timeRaw ? dayjs(timeRaw).format('MMMM D, YYYY h:mm A') : null;
    const error = rowVal.publish_error;

    if (status === 'published') {
        return (
            <Tooltip
                title={time ? `Published at: ${time}` : 'Published successfully'}
                placement="top"
                color="white"
                destroyTooltipOnHide
                overlayInnerStyle={{
                    fontSize: graphXAxisLabelFontSize,
                    color: colorConstants.blackAlt1,
                }}
            >
                <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 16 }} />
            </Tooltip>
        );
    }
    if (status === 'publish_in_progress' && rowVal.is_publishable && !isDeletedRecord) {
        return <SyncOutlined style={{ color: '#44338A', fontSize: 16 }} spin />;
    }
    if (status === 'failed_to_publish' || status === 'publish_failed') {
        return (
            <Tooltip
                title={error ? error : 'Publish failed'}
                placement="top"
                color="white"
                destroyTooltipOnHide
                overlayInnerStyle={{
                    fontSize: graphXAxisLabelFontSize,
                    color: colorConstants.blackAlt1,
                }}
            >
                <ExclamationCircleOutlined style={{ color: '#CA3D3D', fontSize: 16 }} />
            </Tooltip>
        );
    }
    return null;
};

export const getCalenderTableColumns = (
    nonCalenderColumnsMapping: any[],
    calenderColumnsMapping: any[],
    widthObj: { calenderWidth: number; nonCalenderWidth: any; firstLastColumnWidth: any },
    hoverDataMapping: any[],
    onChangeCalenderViewClickHandler: Function,
    showCalenderChanges: boolean,
    calenderOptionsClickHandler: Function,
    openEventsOptionsPopup: any,
    setOpenEventsOptionsPopup: Function,
    isTpmTab: boolean,
    sortObj: { sortBy: any; sortOrder: any },
    viewMode: boolean,
    selectedPlanInfo: SelectedPlanInfoType | null,
    isPublishing: null | string,
    promoConfigDictionary: PromoConfigDictionary,
) => {
    const { firstLastColumnWidth, calenderWidth, nonCalenderWidth } = widthObj;
    const result: any[] = [];
    const calenderColumns = calenderColumnsMapping;
    const weekBorderStyle: any = {
        position: 'absolute',
        top: 0,
        bottom: 0,
        borderRight: `1px dashed ${colorConstants.borderGreyAlt}`,
    };
    function hideDeletedRow(type: eventStatusType) {
        if (type === 'deleted' && showCalenderChanges) return 1;
        if (type === 'deleted') return 0; //if type is deleted and show changes is not activated then hide row
        return 1;
    }

    if (calenderColumns && calenderColumns.length > 0) {
        //empty first column
        result.push({
            title: '',
            dataIndex: 'operation',
            key: 'operation',
            fixed: 'left',
            align: 'center',
            width: firstLastColumnWidth,
            onCell: (record: any, rowIndex: any) => {
                const { type, label, icon, backgroundColor } = record?.operation ?? {};
                return {
                    style: {
                        //    background: type && showCalenderChanges ? backgroundColor : 'white',
                        zIndex: 4,
                        // height: rowIndex === 0 ? 'auto' : calenderMinRowHeight,
                    },
                    rowSpan: hideDeletedRow(record?.operation?.type),
                };
            },
            render: (colVal: any, rowVal: any) => {
                const { type, label, icon, backgroundColor } = rowVal?.operation ?? {};
                if (type && !isTpmTab && showCalenderChanges) {
                    return (
                        <Tooltip title={label} placement="top">
                            {icon}
                        </Tooltip>
                    );
                }
                return null;
            },
        });
        //non calender columns - like events etc
        const nonClickableColumnKeys = [
            'promoStatus',
            'planId',
            'tactic_type_id',
            'promotion_mechanic',
            'tpm_promo_id',
        ];
        const nonCalenderColumns = nonCalenderColumnsMapping?.map((item: any, index: number) => {
            const isColumnClickable = !nonClickableColumnKeys.includes(item?.key) && !showCalenderChanges;
            return {
                title: <p className={`font-xs pl-4 font-bold text-purpleFont`}>{item?.label}</p>,

                dataIndex: item?.key,
                key: item?.key,
                fixed: 'left',
                align: 'left',
                width: nonCalenderWidth,
                sortKey: item?.sortKey, //adding a sort key so that on sort click we can read value from this key
                sortOrder: item?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
                sorter: item?.sortKey ? true : false,
                sortDirections: item?.key === 'event' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in event field
                onCell: (record: any, rowIndex: any) => {
                    const { type, label, icon, backgroundColor } = record?.operation ?? {};

                    return {
                        style: {
                            fontWeight: 500,
                            color: colorConstants.fontGrey,
                            //  backgroundColor: type && showCalenderChanges ? backgroundColor : 'white',
                            position: 'relative',
                            backgroundOpacity: 1,
                            zIndex: 4,
                            borderRight:
                                index === nonCalenderColumnsMapping?.length - 1 &&
                                `1px solid ${colorConstants.greyAlt12}`,
                            // height: rowIndex === 0 ? 'auto' : calenderMinRowHeight,
                        },
                        rowSpan: hideDeletedRow(record?.operation?.type),
                    };
                },
                render: (colVal: any, rowVal: any) => {
                    if (item.key === 'promo' && !colVal && !isTpmTab && !viewMode) {
                        colVal = 'Add Promo';
                    }
                    const condition = !isTpmTab && item.key == 'promoStatus';
                    if (item.key === 'tactic_type_id' && colVal && promoConfigDictionary?.tactics) {
                        colVal = promoConfigDictionary.tactics[Number(colVal)] || colVal;
                    }
                    return (
                        <>
                            <div
                                onClick={() => {
                                    if (isColumnClickable) {
                                        calenderOptionsClickHandler(
                                            rowVal?.eventId,
                                            item?.key === 'promo' ? 'add_promo' : 'edit_event',
                                            rowVal,
                                        );
                                    }
                                }}
                                className={`font-xs break-all flex items-center gap-2 ${
                                    condition ? 'justify-between' : ''
                                } ${isColumnClickable ? 'underline cursor-pointer' : ''}`}
                            >
                                {item.key === 'promo' && rowVal?.keepDates && (
                                    <span>
                                        {' '}
                                        <CalendarOutlined />
                                    </span>
                                )}
                                {item.key === 'promo' && rowVal?.keepPromo && (
                                    <span>
                                        {' '}
                                        <PushpinOutlined />
                                    </span>
                                )}

                                {colVal}
                                {condition && statusIconRenderer(isTpmTab, item, rowVal, isPublishing)}
                            </div>
                        </>
                    );
                },
            };
        });
        result.push(...nonCalenderColumns);

        //calender columns
        const dateColumns =
            (calenderColumns &&
                calenderColumns.length > 0 &&
                calenderColumns.map((item, index) => {
                    return {
                        title: renderTitle(
                            item?.label,
                            undefined,
                            undefined,
                            'text-purpleFont !font-bold !justify-start !pl-0',
                            graphXAxisLabelFontSize,
                        ),
                        sortKey: item?.sortKey, //adding a sort key so that on sort click we can read value from this key
                        sortOrder: item?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
                        sorter: item?.sortKey ? true : false,
                        dataIndex: item?.key,
                        key: item?.key,
                        align: 'left',
                        width: calenderWidth,
                        // colSpan: rowIndex === 0 ? (index === 0 ? keyItems.length + 1 : 0) : 1,
                        onCell: (record: any, rowIndex: any) => {
                            const { type, label, icon, backgroundColor } = record?.operation ?? {};
                            return {
                                style: {
                                    paddingInline: 0,
                                    //   paddingBlock: rowIndex === 0 ? 0 : 2,
                                    verticalAlign: 'top',
                                    borderRight: `1px solid ${colorConstants.borderGreyAlt}`,
                                    //      background: type && showCalenderChanges ? backgroundColor : 'white',
                                },
                                rowSpan: hideDeletedRow(record?.operation?.type),
                            };
                        },
                        render: (colVal: any, rowVal: any, rowIndex: number) => {
                            return (
                                <>
                                    <div className="relative">
                                        {colVal?.length > 0 &&
                                            colVal.map((val: any, index: number) => {
                                                const style = val.style || {};
                                                return (
                                                    <CalenderEventItem
                                                        item={{
                                                            ...val,
                                                            infoData: {
                                                                ...val.infoData,
                                                                division: selectedPlanInfo?.divisionName,
                                                            },
                                                        }}
                                                        pre={''}
                                                        style={style}
                                                        overlayKeyMapping={hoverDataMapping}
                                                    />
                                                );
                                            })}
                                    </div>
                                    <div>
                                        <div style={{ ...weekBorderStyle, width: (calenderWidth / 4) * 1 }}> </div>
                                        <div style={{ ...weekBorderStyle, width: (calenderWidth / 4) * 2 }}> </div>
                                        <div style={{ ...weekBorderStyle, width: (calenderWidth / 4) * 3 }}> </div>
                                    </div>
                                </>
                            );
                        },
                    };
                })) ||
            [];
        result.push(...dateColumns);
        // last Column - options - 3 dots
        result.push({
            title: (
                <div className="flex justify-center ">
                    <Popover
                        trigger={'hover'}
                        overlayInnerStyle={{ borderRadius: 6, position: 'relative', top: -10 }}
                        showArrow={false}
                        placement="bottomRight"
                        content={
                            <p
                                className="font-xs cursor-pointer"
                                onClick={() => {
                                    onChangeCalenderViewClickHandler();
                                }}
                            >
                                Change Calender View
                            </p>
                        }
                    >
                        <span className="p-1 cursor-pointer">
                            <MoreOutlined />
                        </span>
                    </Popover>
                </div>
            ),
            dataIndex: 'collapse',
            key: 'collapse',
            align: 'center',
            fixed: 'right',
            width: firstLastColumnWidth,
            onCell: (record: any, rowIndex: any) => ({
                style: {
                    padding: 0,
                    background: 'white',
                },
                rowSpan: hideDeletedRow(record?.operation?.type),
            }),
            render: (colVal: any, rowVal: any, rowIndex: number) => {
                const isPostAuditOrWriteOff = isPostAuditOrWriteOffPromo(rowVal?.promotion_type_id);
                return (
                    <div className="p-1 cursor-pointer">
                        {isTpmTab || showCalenderChanges || viewMode ? null : (
                            <Popover
                                trigger={'click'}
                                onOpenChange={(open) => {
                                    setOpenEventsOptionsPopup({ eventKey: rowVal?.key, open });
                                }}
                                open={openEventsOptionsPopup?.open && openEventsOptionsPopup?.eventKey === rowVal?.key}
                                overlayInnerStyle={{ borderRadius: 6 }}
                                showArrow={false}
                                placement="bottomRight"
                                content={
                                    <>
                                        <div
                                            onClick={() => {
                                                calenderOptionsClickHandler(rowVal?.eventId, 'keep_dates', rowVal);
                                            }}
                                            className=" cursor-pointer py-2 items-center flex gap-3"
                                        >
                                            <CalendarOutlined />
                                            <p>{rowVal?.keepDates ? 'Remove' : ''} Keep Dates</p>
                                        </div>
                                        <div
                                            onClick={() => {
                                                calenderOptionsClickHandler(rowVal?.eventId, 'keep_promo', rowVal);
                                            }}
                                            className=" cursor-pointer py-2 items-center flex gap-3"
                                        >
                                            <PushpinOutlined />
                                            <p>{rowVal?.keepPromo ? 'Remove' : ''} Keep Promo</p>
                                        </div>
                                        <div
                                            onClick={() => {
                                                calenderOptionsClickHandler(rowVal?.eventId, 'add_promo', rowVal);
                                            }}
                                            className=" cursor-pointer py-2 items-center flex gap-3"
                                        >
                                            <PlusSquareOutlined />
                                            <p>Add/Edit Promo</p>
                                        </div>
                                        {!isPostAuditOrWriteOff && (
                                            <div
                                                onClick={() => {
                                                    calenderOptionsClickHandler(rowVal?.eventId, 'copy_event', rowVal);
                                                }}
                                                className=" cursor-pointer py-2 items-center flex gap-3"
                                            >
                                                <CopyOutlined />
                                                <p>Copy Promo</p>
                                            </div>
                                        )}
                                        <Tooltip
                                            title={
                                                isPostAuditOrWriteOff
                                                    ? 'Edit is not allowed for post audit or write off promos.'
                                                    : ''
                                            }
                                            placement="left"
                                            destroyTooltipOnHide
                                            overlayInnerStyle={{ fontSize: 11 }}
                                        >
                                            <div
                                                onClick={() => {
                                                    if (isPostAuditOrWriteOff) return;
                                                    calenderOptionsClickHandler(rowVal?.eventId, 'edit_event', rowVal);
                                                }}
                                                className={`py-2 items-center flex gap-3 ${
                                                    isPostAuditOrWriteOff
                                                        ? 'cursor-not-allowed text-gray-300'
                                                        : 'cursor-pointer'
                                                }`}
                                            >
                                                <EditOutlined />
                                                <p>Edit Event</p>
                                            </div>
                                            {!isTpmTab && selectedPlanInfo?.isTpmPlan && (
                                                <div
                                                    onClick={() => {
                                                        calenderOptionsClickHandler(
                                                            rowVal?.eventId,
                                                            'view_changes',
                                                            rowVal,
                                                        );
                                                    }}
                                                    className=" cursor-pointer py-2 items-center flex gap-3"
                                                >
                                                    <EyeOutlined />
                                                    <p>View Changes</p>
                                                </div>
                                            )}
                                        </Tooltip>
                                        {/* <div
                                            onClick={() => {
                                                calenderOptionsClickHandler(rowVal?.eventId, 'copy', rowVal);
                                            }}
                                            className=" cursor-pointer py-2 items-center flex gap-3"
                                        >
                                            <CopyOutlined />
                                            <p>Copy</p>
                                        </div> */}
                                        <Tooltip
                                            title={
                                                isPostAuditOrWriteOff
                                                    ? 'Delete is not allowed for post audit or write off promos.'
                                                    : ''
                                            }
                                            placement="left"
                                            destroyTooltipOnHide
                                            overlayInnerStyle={{ fontSize: 11 }}
                                        >
                                            <div
                                                onClick={() => {
                                                    if (isPostAuditOrWriteOff) return;
                                                    calenderOptionsClickHandler(rowVal?.eventId, 'delete', rowVal);
                                                }}
                                                className={`py-2 items-center flex gap-3 ${
                                                    isPostAuditOrWriteOff
                                                        ? 'cursor-not-allowed text-gray-300'
                                                        : 'cursor-pointer text-redAlt'
                                                }`}
                                            >
                                                <DeleteOutlined />
                                                <p>Delete Event</p>
                                            </div>
                                        </Tooltip>
                                    </>
                                }
                            >
                                <MoreOutlined />
                            </Popover>
                        )}
                    </div>
                );
            },
        });
    }

    return result;
};

export const getKpiTableColumns = (
    kpiColumns: any[],
    nonKpiColumns: any[],
    width: number,
    firstLastColumnWidth: number,
    setOpenEventsOptionsPopup: Function,
    openEventsOptionsPopup: any,
    calenderOptionsClickHandler: Function,
    commonConfig: any,
    isKpiAbsView: boolean,
    tableSubHeader: string[],
) => {
    const result: any[] = [];
    //non kpi columns like events etc
    const nonKpiTableColumns = nonKpiColumns?.map((item: any, index: number) => {
        return {
            title: <p className={`font-xs font-bold text-purpleFont`}>{item?.label}</p>,

            dataIndex: item?.key,
            key: item?.key,
            fixed: 'left',
            align: 'left',
            width: width - 110,
            sortKey: item?.sortKey, //adding a sort key so that on sort click we can read value from this key
            // sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: item?.sortKey ? true : false,
            // sortDirections: item?.key === 'updated_at' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            onCell: (record: any, rowIndex: any) => {
                if (rowIndex === 0)
                    return {
                        style: {
                            paddingBlock: 4,
                            borderRight: index === nonKpiColumns?.length - 1 && `1px solid ${colorConstants.greyAlt12}`,
                        },
                    };
                return {
                    style: {
                        borderRight: index === nonKpiColumns?.length - 1 && `1px solid ${colorConstants.greyAlt12}`,
                        // height: rowIndex === 0 ? 'auto' : calenderMinRowHeight,
                    },
                };
            },
            render: (colVal: any, rowVal: any, rowIndex: number) => {
                if (rowIndex === 0) return null;

                // if (item.key === 'promo_name' && !colVal) {
                //     colVal = 'Add Promo';
                // }
                return (
                    <>
                        <div
                            // onClick={() => {
                            //     if (from === 'event_library') {
                            //         calenderOptionsClickHandler(
                            //             rowVal?.eventId,
                            //             item?.key === 'promo_name' ? 'add_promo' : 'edit_event',
                            //             rowVal,
                            //         );
                            //     }
                            // }}
                            className={`font-xs`}
                        >
                            {colVal}
                        </div>
                    </>
                );
            },
        };
    });
    result.push(...nonKpiTableColumns);

    const { decimalForPercentage, decimalForNonPercentage } = commonConfig;
    //kpi columns like total profit etc
    const kpiTableColumns = kpiColumns?.map((item: any, index: number) => {
        return {
            title: <p className={`font-xs font-bold text-purpleFont`}>{item?.label}</p>,
            dataIndex: item?.key,
            key: item?.key,
            fixed: false,
            align: 'left',
            width: width,
            onCell: (record: any, rowIndex: number) => {
                if (rowIndex === 0)
                    return {
                        style: { paddingBlock: 4 },
                    };
            },
            sortKey: item?.sortKey, //adding a sort key so that on sort click we can read value from this key
            // sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: item?.sortKey ? true : false,
            // sortDirections: item?.key === 'updated_at' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            render: (colVal: any, rowVal: any, rowIndex: number) => {
                const { pre, post, allowedDecimals, value1, value2, absChange, percentageChange } = colVal ?? {};
                const centerValue = isKpiAbsView ? absChange : percentageChange;
                const percentageValueColor =
                    !_.isNil(percentageChange) && percentageChange === 0
                        ? colorConstants.greyAlt14
                        : percentageChange > 0
                        ? colorConstants.limeGreen
                        : colorConstants.redAlt;
                //adding table sub header for columns
                if (rowIndex === 0) {
                    return (
                        <div className="max-w-[300px] flex items-center justify-between">
                            {tableSubHeader?.map((header: string) => {
                                return <p className="basis-2/5 whitespace-nowrap text-fontGreyAlt">{header}</p>;
                            })}
                        </div>
                    );
                }

                if (_.isNil(value1)) {
                    return null;
                }
                return (
                    <div className="font-xs flex max-w-[300px] justify-between items-center">
                        <p className="basis-2/5">
                            {pre}
                            {typeof value1 === 'number' ? formatNumberWithCommas(value1, allowedDecimals) : value1}
                            {/* {formatNumWithKMBAlt(
                                value1,
                                post === '%' ? decimalForPercentage : decimalForNonPercentage,
                                post === '%' ? undefined : decimalForNonPercentage,
                            )} */}
                            {post}
                        </p>
                        {!_.isNil(centerValue) && (
                            <p className="basis-2/5" style={{ color: percentageValueColor }}>
                                {formatNumberWithCommas(
                                    centerValue,
                                    !isKpiAbsView ? decimalForPercentage : allowedDecimals,
                                )}
                                {/* {formatNumWithKMBAlt(
                                    centerValue,
                                    !isKpiAbsView ? decimalForPercentage : decimalForNonPercentage,
                                    !isKpiAbsView ? undefined : decimalForNonPercentage,
                                )} */}
                                {!isKpiAbsView ? '%' : ''}
                            </p>
                        )}

                        {!_.isNil(value2) && (
                            <p className="basis-2/5 text-fontGreyAlt">
                                {pre}
                                {typeof value2 === 'number' ? formatNumberWithCommas(value2, allowedDecimals) : value2}
                                {/* {formatNumWithKMBAlt(
                                    value2,
                                    post === '%' ? decimalForPercentage : decimalForNonPercentage,
                                    post === '%' ? undefined : decimalForNonPercentage,
                                )} */}
                                {post}
                            </p>
                        )}
                    </div>
                );
            },
        };
    });
    result.push(...kpiTableColumns);

    // last Column - options - 3 dots
    // {
    //     from === 'plan' &&
    //         result.push({
    //             title: '',
    //             dataIndex: 'collapse',
    //             key: 'collapse',
    //             align: 'center',
    //             width: firstLastColumnWidth,
    //             onCell: (record: any, rowIndex: any) => ({
    //                 style: {
    //                     padding: 0,
    //                     background: 'white',
    //                 },
    //             }),
    //             render: (colVal: any, rowVal: any, rowIndex: number) => {
    //                 return (
    //                     <div className="p-1 cursor-pointer">
    //                         {from === 'plan' ? null : (
    //                             <Popover
    //                                 trigger={'click'}
    //                                 onOpenChange={(open) => {
    //                                     setOpenEventsOptionsPopup({
    //                                         eventKey: rowVal?.eventId,
    //                                         open,
    //                                         promoKey: rowVal?.promoId,
    //                                     });
    //                                 }}
    //                                 open={
    //                                     openEventsOptionsPopup?.open &&
    //                                     openEventsOptionsPopup?.eventKey === rowVal?.eventId &&
    //                                     openEventsOptionsPopup?.promoKey === rowVal?.promoId
    //                                 }
    //                                 overlayStyle={{ paddingTop: 0 }}
    //                                 overlayInnerStyle={{ borderRadius: 6 }}
    //                                 showArrow={false}
    //                                 placement="bottomRight"
    //                                 content={
    //                                     <>
    //                                         <div
    //                                             onClick={() => {
    //                                                 calenderOptionsClickHandler(rowVal?.eventId, 'add_promo', rowVal);
    //                                             }}
    //                                             className=" cursor-pointer py-2 items-center flex gap-3"
    //                                         >
    //                                             <PlusSquareOutlined />
    //                                             <p>Add/Edit Promo</p>
    //                                         </div>
    //                                         <div
    //                                             onClick={() => {
    //                                                 calenderOptionsClickHandler(rowVal?.eventId, 'edit_event', rowVal);
    //                                             }}
    //                                             className=" cursor-pointer py-2 items-center flex gap-3"
    //                                         >
    //                                             <EditOutlined />
    //                                             <p>Edit Event</p>
    //                                         </div>

    //                                         {/* <div
    //                                     onClick={() => {
    //                                         calenderOptionsClickHandler(rowVal?.eventId, 'delete', rowVal);
    //                                     }}
    //                                     className="text-redAlt  cursor-pointer py-2 items-center flex gap-3"
    //                                 >
    //                                     <DeleteOutlined />
    //                                     <p>Delete</p>
    //                                 </div> */}
    //                                     </>
    //                                 }
    //                             >
    //                                 <MoreOutlined />
    //                             </Popover>
    //                         )}
    //                     </div>
    //                 );
    //             },
    //         });
    // }

    return result;
};

export const getPlanSummaryTableColumns = (
    kpiColumns: any[],
    nonKpiColumns: any[],
    width: number,
    commonConfig: any,
    isKpiAbsView: boolean,
    from: 'top' | 'bottom',
    sortObj?: { sortBy: any; sortOrder: any },
) => {
    const result: any[] = [];
    //non kpi columns like events etc
    const nonKpiTableColumns = nonKpiColumns?.map((item: any, index: number) => {
        return {
            title: <p className={`font-xs font-bold text-purpleFont`}>{item?.label}</p>,

            dataIndex: item?.key,
            key: item?.key,
            fixed: 'left',
            align: 'left',
            width: width,
            sortKey: from === 'top' ? undefined : item?.sortKey, //adding a sort key so that on sort click we can read value from this key
            sortOrder: item?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: from === 'top' ? undefined : item?.sortKey ? true : false,
            // sortDirections: item?.key === 'updated_at' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            onCell: (record: any, rowIndex: any) => {
                return {
                    style: {
                        paddingBlock: 4,
                        borderRight: index === nonKpiColumns?.length - 1 && `1px solid ${colorConstants.greyAlt12}`,
                        // height: rowIndex === 0 ? 'auto' : calenderMinRowHeight,
                    },
                };
            },
            render: (colVal: any, rowVal: any, rowIndex: number) => {
                // if (item.key === 'promo_name' && !colVal) {
                //     colVal = 'Add Promo';
                // }
                return (
                    <>
                        <div
                            // onClick={() => {
                            //     if (from === 'event_library') {
                            //         calenderOptionsClickHandler(
                            //             rowVal?.eventId,
                            //             item?.key === 'promo_name' ? 'add_promo' : 'edit_event',
                            //             rowVal,
                            //         );
                            //     }
                            // }}
                            className={`font-xs`}
                        >
                            {rowVal?.key === 'total-row' ? (
                                <p className="whitespace-nowrap text-fontGreyAlt">{colVal}</p>
                            ) : (
                                <>{colVal}</>
                            )}
                        </div>
                    </>
                );
            },
        };
    });
    result.push(...nonKpiTableColumns);

    const { decimalForPercentage, decimalForNonPercentage } = commonConfig;
    //kpi columns like total profit etc
    const kpiTableColumns = kpiColumns?.map((item: any, index: number) => {
        return {
            title: <p className={`font-xs font-bold text-purpleFont`}>{item?.label}</p>,
            dataIndex: item?.key,
            key: item?.key,
            fixed: false,
            align: 'left',
            width: width - 20,
            onCell: (record: any, rowIndex: number) => {
                return {
                    style: { paddingBlock: 4 },
                };
            },
            sortKey: from === 'top' ? undefined : item?.sortKey, //adding a sort key so that on sort click we can read value from this key
            sortOrder: item?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: from === 'top' ? undefined : item?.sortKey ? true : false,
            // sortDirections: item?.key === 'updated_at' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            render: (colVal: any, rowVal: any, rowIndex: number) => {
                const { pre, post, allowedDecimals, value1, value2, absChange, percentageChange } = colVal ?? {};
                const centerValue = isKpiAbsView ? absChange : percentageChange;
                const percentageValueColor =
                    !_.isNil(percentageChange) && percentageChange === 0
                        ? colorConstants.greyAlt14
                        : percentageChange > 0
                        ? colorConstants.limeGreen
                        : colorConstants.redAlt;

                if (_.isNil(value1)) {
                    return null;
                }
                return (
                    <div className="font-xs flex max-w-[300px] justify-between items-center">
                        <p className="basis-2/5">
                            {rowVal?.key === 'total-row' ? (
                                <p className="whitespace-nowrap text-fontGreyAlt">
                                    {pre}
                                    {typeof value1 === 'number'
                                        ? formatNumberWithCommas(value1, allowedDecimals)
                                        : value1}
                                    {post}
                                </p>
                            ) : (
                                <>
                                    {pre}
                                    {typeof value1 === 'number'
                                        ? formatNumberWithCommas(value1, allowedDecimals)
                                        : value1}
                                    {post}
                                </>
                            )}
                        </p>
                        {!_.isNil(centerValue) && (
                            <p className="basis-2/5" style={{ color: percentageValueColor }}>
                                {formatNumberWithCommas(
                                    centerValue,
                                    !isKpiAbsView ? decimalForPercentage : allowedDecimals,
                                )}
                                {/* {formatNumWithKMBAlt(
                                    centerValue,
                                    !isKpiAbsView ? decimalForPercentage : decimalForNonPercentage,
                                    !isKpiAbsView ? undefined : decimalForNonPercentage,
                                )} */}
                                {!isKpiAbsView ? '%' : ''}
                            </p>
                        )}

                        {!_.isNil(value2) && (
                            <p className="basis-2/5 text-fontGreyAlt">
                                {pre}
                                {typeof value2 === 'number' ? formatNumberWithCommas(value2, allowedDecimals) : value2}
                                {/* {formatNumWithKMBAlt(
                                    value2,
                                    post === '%' ? decimalForPercentage : decimalForNonPercentage,
                                    post === '%' ? undefined : decimalForNonPercentage,
                                )} */}
                                {post}
                            </p>
                        )}
                    </div>
                );
            },
        };
    });
    result.push(...kpiTableColumns);

    // last Column - options - 3 dots
    // {
    //     from === 'plan' &&
    //         result.push({
    //             title: '',
    //             dataIndex: 'collapse',
    //             key: 'collapse',
    //             align: 'center',
    //             width: firstLastColumnWidth,
    //             onCell: (record: any, rowIndex: any) => ({
    //                 style: {
    //                     padding: 0,
    //                     background: 'white',
    //                 },
    //             }),
    //             render: (colVal: any, rowVal: any, rowIndex: number) => {
    //                 return (
    //                     <div className="p-1 cursor-pointer">
    //                         {from === 'plan' ? null : (
    //                             <Popover
    //                                 trigger={'click'}
    //                                 onOpenChange={(open) => {
    //                                     setOpenEventsOptionsPopup({
    //                                         eventKey: rowVal?.eventId,
    //                                         open,
    //                                         promoKey: rowVal?.promoId,
    //                                     });
    //                                 }}
    //                                 open={
    //                                     openEventsOptionsPopup?.open &&
    //                                     openEventsOptionsPopup?.eventKey === rowVal?.eventId &&
    //                                     openEventsOptionsPopup?.promoKey === rowVal?.promoId
    //                                 }
    //                                 overlayStyle={{ paddingTop: 0 }}
    //                                 overlayInnerStyle={{ borderRadius: 6 }}
    //                                 showArrow={false}
    //                                 placement="bottomRight"
    //                                 content={
    //                                     <>
    //                                         <div
    //                                             onClick={() => {
    //                                                 calenderOptionsClickHandler(rowVal?.eventId, 'add_promo', rowVal);
    //                                             }}
    //                                             className=" cursor-pointer py-2 items-center flex gap-3"
    //                                         >
    //                                             <PlusSquareOutlined />
    //                                             <p>Add/Edit Promo</p>
    //                                         </div>
    //                                         <div
    //                                             onClick={() => {
    //                                                 calenderOptionsClickHandler(rowVal?.eventId, 'edit_event', rowVal);
    //                                             }}
    //                                             className=" cursor-pointer py-2 items-center flex gap-3"
    //                                         >
    //                                             <EditOutlined />
    //                                             <p>Edit Event</p>
    //                                         </div>

    //                                         {/* <div
    //                                     onClick={() => {
    //                                         calenderOptionsClickHandler(rowVal?.eventId, 'delete', rowVal);
    //                                     }}
    //                                     className="text-redAlt  cursor-pointer py-2 items-center flex gap-3"
    //                                 >
    //                                     <DeleteOutlined />
    //                                     <p>Delete</p>
    //                                 </div> */}
    //                                     </>
    //                                 }
    //                             >
    //                                 <MoreOutlined />
    //                             </Popover>
    //                         )}
    //                     </div>
    //                 );
    //             },
    //         });
    // }

    return result;
};

export const getImportedEventsTableColumns = (
    columns: any[],
    data: any[],
    checkboxSelected: any,
    setCheckboxSelected: Function,
    eventsDataCount: number,
) => {
    let result: any[] = [];
    if (columns?.length)
        //checkbox
        result.push({
            title: (
                <Checkbox
                    checked={'selectAll' in checkboxSelected}
                    onChange={(e) => {
                        //if select all is checked, add all keys and select all key
                        if (e.target.checked) {
                            const temp: any = {};
                            data?.forEach((item: any) => {
                                if (item?.eventId in temp) {
                                    temp[item?.eventId]?.push(item?.promoId);
                                } else {
                                    temp[item?.eventId] = [item?.promoId];
                                }
                            });
                            setCheckboxSelected({ selectAll: true, ...temp });
                        } else {
                            setCheckboxSelected({});
                        }
                    }}
                />
            ),
            dataIndex: 'eventId',
            key: 'eventId',
            width: 50,
            render: (columnData: any, rowData: any) => {
                const { key, eventId, promoId } = rowData;
                const isSelected =
                    checkboxSelected && eventId in checkboxSelected && checkboxSelected[eventId]?.includes(promoId);
                //checkbox
                return (
                    <div>
                        <Checkbox
                            checked={isSelected}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    const temp = { ...checkboxSelected };
                                    if (eventId in temp) {
                                        temp[eventId]?.push(promoId);
                                    } else {
                                        temp[eventId] = [promoId];
                                    }
                                    // if (Object.keys(temp).length === data?.length - 1) {
                                    //     temp['selectAll'] = true;
                                    // }
                                    const selectedItems: number = Object.values(temp).reduce(
                                        (total: number, promoIds: any) => total + (promoIds?.length ?? 0),
                                        0,
                                    );
                                    //if all items are manually selected then enable select all button
                                    if (selectedItems === eventsDataCount) temp['selectAll'] = true;
                                    setCheckboxSelected({ ...temp });
                                } else {
                                    const temp = { ...checkboxSelected };
                                    delete temp?.selectAll;
                                    if (!temp[eventId]) return temp; // If eventId does not exist, return original data

                                    // Filter out the promoId from the array
                                    temp[eventId] = temp[eventId].filter((id: any) => id !== promoId);

                                    // If no ID remains, delete the eventId key
                                    if (temp[eventId].length === 0) {
                                        delete temp[eventId];
                                    }

                                    setCheckboxSelected({ ...temp });
                                }
                            }}
                        />
                    </div>
                );
            },
        });
    const tempColumns = columns?.map((header: any) => {
        const filterOptions: any = {};
        if (header?.filterKey) {
            const set = new Set();
            data.forEach((item: any) => {
                set.add(item[header?.filterKey]);
            });
            filterOptions[header?.filterKey] = Array.from(set).map((item: any) => ({
                text: item,
                value: item,
            }));
        }
        return {
            title: <p className={`text-[14px] text-purpleFont`}>{header?.label}</p>,
            dataIndex: header?.key,
            key: header?.key,
            onCell: (record: any, rowIndex: number) => {},
            // sortKey: header?.sortKey, //adding a sort key so that on sort click we can read value from this key
            // sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: header?.sortKey
                ? (a: any, b: any) => {
                      const aValue = a[header?.sortKey];
                      const bValue = b[header?.sortKey];

                      // Handle null/undefined values
                      if (aValue == null && bValue == null) return 0;
                      if (aValue == null) return 1;
                      if (bValue == null) return -1;

                      // Handle strings
                      if (typeof aValue === 'string' && typeof bValue === 'string') {
                          return aValue.localeCompare(bValue);
                      }

                      // Handle numbers
                      if (typeof aValue === 'number' && typeof bValue === 'number') {
                          return aValue - bValue;
                      }

                      // Default string comparison
                      return String(aValue).localeCompare(String(bValue));
                  }
                : false,
            filters: header?.filterKey ? filterOptions[header?.filterKey] : undefined,
            onFilter: (value: any, record: any) =>
                header?.filterKey && record[header?.filterKey].indexOf(value as string) === 0,
            // sortDirections: header?.key === 'updated_at' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            // width: 150,
            render: (columnData: any, rowData: any) => {
                return <div>{columnData}</div>;
            },
        };
    });
    result.push(...tempColumns);
    return result;
};
export const getEventsLibraryColumns = (columns: any[], pathname: string) => {
    let result: any[] = [];

    const tempColumns = columns?.map((header: any) => {
        return {
            title: <p className={`text-[14px] text-purpleFont`}>{header?.label}</p>,
            dataIndex: header?.key,
            key: header?.key,
            onCell: (record: any, rowIndex: number) => {},
            // sortKey: header?.sortKey, //adding a sort key so that on sort click we can read value from this key
            // sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            // sorter: header?.sortKey ? true : false,
            // sortDirections: header?.key === 'updated_at' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            // width: 150,
            render: (columnData: any, rowData: any) => {
                const { isViewMode, planId } = rowData;
                if (header?.key === 'plan') {
                    const url = `${pathname}/${isViewMode ? 'view' : 'update'}/${planId}`;
                    return (
                        <div className="flex gap-2 items-center">
                            {/* <span className="mr-2 ">
                                <Checkbox disabled={status === 'expired'} />
                            </span> */}
                            <Link className="text-blackAlt underline" to={url}>
                                {columnData}
                            </Link>
                        </div>
                    );
                }
                return <div>{columnData}</div>;
            },
        };
    });
    result.push(...tempColumns);
    return result;
};

export const getPlaybookColumns = (attributeTypesColumns: any[], kpiColumns: any[], onClickHandler: Function) => {
    let result: any[] = [];

    const attributeTypesColumn = attributeTypesColumns?.map((header: any) => {
        return {
            title: <p className={`text-[14px] text-purpleFont`}>{header?.label}</p>,
            dataIndex: header?.label,
            key: header?.key,
            onCell: (record: any, rowIndex: number) => {},
            sortKey: header?.sortKey, //adding a sort key so that on sort click we can read value from this key
            // sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: header?.sortKey ? true : false,
            sortDirections: header?.key === 'updated_at' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            width: 200,
            fixed: 'left',
            render: (columnData: any, rowData: any) => {
                return <div className="font-xs">{columnData}</div>;
            },
        };
    });

    const tempColumns = kpiColumns?.map((header: any) => {
        return {
            title: (
                <p
                    onClick={() => {
                        onClickHandler(header?.kpiInfo);
                    }}
                    className={`text-[14px] cursor-pointer underline text-purpleFont`}
                >
                    {header?.label}
                </p>
            ),
            dataIndex: header?.key,
            key: header?.key,
            onCell: (record: any, rowIndex: number) => {},
            sortKey: header?.sortKey, //adding a sort key so that on sort click we can read value from this key
            //   sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: header?.sortKey ? true : false,
            sortDirections: header?.key === 'updated_at' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            width: 150,
            render: (columnData: any, rowData: any) => {
                const { guidelines } = columnData ?? {};
                return (
                    <div
                        onClick={() => {
                            onClickHandler({
                                ...header?.kpiInfo,
                                values: columnData?.values,
                                label: rowData?.[attributeTypesColumns?.[0]?.label],
                                rowId: rowData?.key,
                            });
                        }}
                        className="font-xs cursor-pointer"
                    >
                        {guidelines}
                    </div>
                );
            },
        };
    });
    result.push(...attributeTypesColumn, ...tempColumns);
    return result;
};

export const getPromoLibraryTableColumns = (
    attributeTypesColumns: any[],
    kpiColumns: any[],
    onEdit: Function,
    sortObj: any,
    showChanges: boolean,
    isAbsoluteView: boolean,
    fixedColumns: string[] = [],
    kpiFilters: Record<string, KpiFilterValue> = {},
    onKpiFilter?: (kpiKey: string, filter: KpiFilterValue | null) => void,
) => {
    let result: any[] = [];
    // When `fixedColumns` is provided, only the columns whose key matches an entry in the array
    // are pinned to the left. Otherwise, fall back to the legacy behavior where every attribute
    // column is pinned and KPI columns are not.
    const isFixedKey = (key?: string) => (key ? fixedColumns!.includes(key) : false);

    const attributeTypesColumn = attributeTypesColumns?.map((header: any) => ({
        title: <p className={`font-xs text-purpleFont`}>{header?.label}</p>,
        dataIndex: header?.label,
        key: header?.key,
        sortKey: header?.sortKey, //adding a sort key so that on sort click we can read value from this key
        sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
        sorter: header?.sortKey ? true : false,
        sortDirections: ['ascend', 'descend', 'ascend'], //always have some sorting in last updated at field
        width: 200,
        fixed: isFixedKey(header?.key) ? 'left' : undefined,
        onCell: (record: any, rowIndex: number) => {
            return {
                style: {
                    backgroundColor: isFixedKey(header?.key) ? 'white' : colorConstants.greyAlt19,
                },
            };
        },
        render: (columnData: any, rowData: any) => {
            return <div className="font-xs">{columnData}</div>;
        },
    }));

    const kpiFormattedColumns = kpiColumns?.map((header: any) => {
        const isEditable = header?.kpiInfo?.isEditable;
        const isSingleInteger = header?.kpiInfo?.type === 'integer' && header?.kpiInfo?.storageType === 'single';
        const isBoolean = header?.kpiInfo?.type === 'boolean';
        const kpiKey = header?.kpiInfo?.value;
        const isFixedKpi = isFixedKey(kpiKey);
        const filterOperators: string[] = header?.kpiInfo?.filterOperators ?? [];
        const hasFilter = filterOperators.length > 0 && !!onKpiFilter;
        return {
            title: (
                <div className="flex items-center gap-1">
                    {hasFilter && (
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{ flexShrink: 0, lineHeight: 1 }}
                        >
                            <KpiColumnFilterPopover
                                kpiKey={kpiKey}
                                filterOperators={filterOperators}
                                activeFilter={kpiFilters[kpiKey]}
                                onApply={onKpiFilter}
                            />
                        </span>
                    )}
                    <p
                        onClick={(e) => {
                            e.stopPropagation();
                            if (isEditable)
                                onEdit(
                                    'column',
                                    { [header?.kpiInfo?.['value']]: { ...header?.kpiInfo } },
                                    undefined,
                                    undefined,
                                );
                        }}
                        className={`font-xs text-purpleFont ${isEditable ? 'cursor-pointer' : ''}`}
                    >
                        {header?.label}
                    </p>
                </div>
            ),
            dataIndex: header?.key,
            key: header?.key,
            sortKey: header?.sortKey, //adding a sort key so that on sort click we can read value from this key
            sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            sorter: header?.sortKey ? true : false,
            sortDirections: ['ascend', 'descend', 'ascend'], //always have some sorting in last updated at field
            width: isEditable ? 180 : isSingleInteger || isBoolean ? (hasFilter ? 160 : 120) : hasFilter ? 220 : 200,
            fixed: isFixedKpi ? 'left' : undefined,
            onCell: (record: any, rowIndex: number) => {
                // Editable cells and fixed/sticky columns get a solid white background; all
                // other (read-only, scrolling) columns get the grey background.
                return {
                    style: {
                        backgroundColor: isEditable || isFixedKpi ? 'white' : colorConstants.greyAlt19,
                    },
                };
            },
            render: (columnData: any, rowData: any) => {
                const {
                    newValue,
                    kpiConfigObject,
                    unformattedNewValue,
                    absoluteChangeValue,
                    originalValue,
                    percentageChangeValue,
                } = columnData || {};
                const dynamicKey = Object.keys(kpiConfigObject || {})[0];
                const {
                    [dynamicKey]: { isEditable, type, storageType },
                } = kpiConfigObject || {};
                const widthClass = 'max-w-[180px]';
                if (type === 'boolean') {
                    return (
                        <div
                            className={`${widthClass} flex items-center gap-2 ${isEditable ? 'cursor-pointer' : ''}`}
                            onClick={() => {
                                if (isEditable) {
                                    onEdit(
                                        'cell',
                                        { ...(kpiConfigObject || {}) },
                                        unformattedNewValue,
                                        rowData?.rowId,
                                        '',
                                    );
                                }
                            }}
                        >
                            <span className={isEditable ? 'basis-2/5' : 'basis-full'}>
                                {_.startCase(String(newValue?.[0]))}
                            </span>
                            {isEditable && showChanges ? (
                                <span className="basis-1/5 text-fontGreyAlt2">
                                    {_.startCase(String(originalValue?.[0]))}
                                </span>
                            ) : null}
                        </div>
                    );
                }
                return (
                    <div
                        onClick={() => {
                            if (isEditable)
                                onEdit('cell', { ...(kpiConfigObject || {}) }, unformattedNewValue, rowData?.rowId, '');
                        }}
                        className={`${widthClass} flex items-center gap-2 font-xs ${
                            _.isNil(newValue?.[0]) ? 'min-h-[40px]' : ''
                        }  ${isEditable ? 'cursor-pointer' : ''}`}
                    >
                        <span className={isEditable ? 'basis-2/5' : 'basis-full'} style={{ wordBreak: 'break-word' }}>
                            {newValue?.join(' - ')}
                        </span>
                        {isEditable && showChanges ? (
                            <>
                                <span
                                    className={`text-fontGreyAlt basis-1/5 ${
                                        percentageChangeValue === 0
                                            ? 'text-fontGreyAlt2'
                                            : percentageChangeValue > 0
                                            ? 'text-successGreen'
                                            : 'text-redAlt'
                                    }`}
                                >
                                    {['range', 'select', 'text'].includes(storageType)
                                        ? null
                                        : isAbsoluteView
                                        ? absoluteChangeValue
                                        : percentageChangeValue + '%'}
                                </span>
                                <span className="text-fontGreyAlt2 basis-2/5" style={{ wordBreak: 'break-word' }}>
                                    {originalValue?.join(' - ')}
                                </span>
                            </>
                        ) : null}
                    </div>
                );
            },
        };
    });
    // If a config attribute carries `insertAfterKpiKey` and that KPI exists on this tab, splice ALL
    // attribute columns into kpiFormattedColumns right after the anchor KPI (and drop their fixed-left
    // pin). Otherwise, fall through to the legacy layout: attribute columns first, KPI columns after.
    const anchor = attributeTypesColumns?.find((a: any) => a?.insertAfterKpiKey);
    const anchorKpiIdx = anchor
        ? kpiColumns?.findIndex((k: any) => k?.kpiInfo?.value === anchor.insertAfterKpiKey) ?? -1
        : -1;

    if (anchorKpiIdx !== -1) {
        const inlineCols = (attributeTypesColumn ?? []).map(({ fixed, ...rest }: any) => rest);
        kpiFormattedColumns?.splice(anchorKpiIdx + 1, 0, ...(inlineCols as any[]));
        result.push(...(kpiFormattedColumns ?? []));
    } else {
        result.push(...(attributeTypesColumn ?? []), ...(kpiFormattedColumns ?? []));
    }

    return result;
};

export const getMechanicsLibraryTableColumns = (data: any[]) => {
    const columns = Object.keys(data?.[0]).slice(1);
    let result: any[] = [];

    const attributeTypesColumn = columns?.map((header: any, index: number) => {
        return {
            title: <p className={`font-xs text-purpleFont`}>{header}</p>,
            dataIndex: header,
            key: header,
            width: 200,
            fixed: index < 2 ? 'left' : undefined,
            render: (columnData: any, rowData: any) => {
                return <div className="font-xs">{columnData}</div>;
            },
        };
    });
    result.push(...attributeTypesColumn);
    return result;
};

export const getPlanComparisonTableColumns = (data: any[]) => {
    const columns = Object.keys(data?.[0]).slice(1);
    let result: any[] = [];

    const attributeTypesColumn = columns?.map((header: any, index: number) => {
        return {
            title: (
                <p className={`font-xs text-purpleFont flex items-center`}>
                    {index === 1 && (
                        <Tag style={{ borderRadius: 4 }} color={'processing'}>
                            B
                        </Tag>
                    )}

                    <span style={{ wordBreak: 'break-word' }}>{header}</span>
                </p>
            ),
            onCell: (record: any, rowIndex: number) => {
                if (rowIndex === 0)
                    return {
                        style: { paddingBlock: 4 },
                    };
            },
            dataIndex: header,
            key: header,
            width: index === 0 ? 250 : 265,
            fixed: index === 0 ? 'left' : undefined,
            render: (columnData: any, rowData: any, rowIndex: number, columnIndex: number) => {
                //adding table sub header for columns
                if (rowIndex === 0) {
                    //kpi label, doht show anything
                    if (index === 0) {
                        return null;
                    }
                    //for base plan , only actual data is present
                    if (index === 1) {
                        return <p className="basis-2/5 text-fontGreyAlt">Actual</p>;
                    }
                    if (rowData?.key === 'comparison-table-header') {
                        return (
                            <div className="max-w-[250px] flex items-center justify-between">
                                <p className="basis-2/5 text-fontGreyAlt">Actual</p>
                                <p className="basis-2/5 text-fontGreyAlt">Diff (%)</p>
                                <p className="basis-2/5 text-fontGreyAlt">Diff (abs)</p>
                            </div>
                        );
                    }
                }
                if (header === 'KPI') {
                    return <div className="font-xs">{columnData}</div>;
                }
                const { value1, value2, value3, color } = columnData;

                return (
                    <div className="font-xs  max-w-[250px] flex items-center justify-between">
                        <p className=" basis-2/5">{value1}</p>
                        <p className=" basis-2/5" style={{ color }}>
                            {value2}
                        </p>
                        <p className=" text-fontGreyAlt basis-2/5">{value3}</p>
                    </div>
                );
            },
        };
    });
    result.push(...attributeTypesColumn);
    return result;
};

/**
 * RBAC: canManage controls whether the action column (edit/delete) is included.
 * When the user lacks write permissions, canManage is false and only data
 * columns are rendered — preventing unauthorized mutation attempts from the UI.
 */
export const getCustomProductGroupColumns = (
    openEditModal: (record: any) => void,
    handleDelete: (record: any) => void,
    canManage: boolean = true,
) => {
    const baseCols = [
        {
            title: <p className="font-xs text-purpleFont">Customer</p>,
            dataIndex: 'customer',
            key: 'customer',
            sorter: (a: any, b: any) => (a.customer ?? '').localeCompare(b.customer ?? ''),
        },
        {
            title: <p className="font-xs text-purpleFont">Group Name</p>,
            dataIndex: 'group_name',
            key: 'group_name',
            sorter: (a: any, b: any) => (a.group_name ?? '').localeCompare(b.group_name ?? ''),
        },
        {
            title: <p className="font-xs text-purpleFont">Material Group ID</p>,
            dataIndex: 'material_group_id',
            key: 'material_group_id',
            sorter: (a: any, b: any) => a.material_group_id - b.material_group_id,
        },
        {
            title: <p className="font-xs text-purpleFont">Material Group</p>,
            dataIndex: 'material_group',
            key: 'material_group',
            sorter: (a: any, b: any) => (a.material_group ?? '').localeCompare(b.material_group ?? ''),
        },
        {
            title: <p className="font-xs text-purpleFont">GP PPG</p>,
            dataIndex: 'product_group',
            key: 'product_group',
            sorter: (a: any, b: any) => (a.product_group ?? '').localeCompare(b.product_group ?? ''),
        },
        {
            title: <p className="font-xs text-purpleFont">Last Updated</p>,
            dataIndex: 'last_updated',
            key: 'last_updated',
            sorter: (a: any, b: any) => (a.last_updated ?? '').localeCompare(b.last_updated ?? ''),
            render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
        },
    ];

    if (!canManage) return baseCols;

    return [
        ...baseCols,
        {
            title: '',
            key: 'row_actions',
            width: 48,
            render: (_: any, record: any) => (
                <Dropdown
                    trigger={['click']}
                    menu={{
                        style: { borderRadius: 8, padding: 4 },
                        items: [
                            {
                                key: 'edit',
                                label: (
                                    <div className="font-xs cursor-pointer" style={{ color: '#262626' }}>
                                        Edit Record
                                    </div>
                                ),
                                onClick: () => openEditModal(record),
                            },
                            {
                                key: 'delete',
                                label: (
                                    <div className="font-xs cursor-pointer" style={{ color: '#ff4d4f' }}>
                                        Delete Record
                                    </div>
                                ),
                                onClick: () => handleDelete(record),
                            },
                        ],
                    }}
                >
                    <StyledEllipsisButton type="text" icon={<EllipsisOutlined />} />
                </Dropdown>
            ),
        },
    ];
};

export const getCustomProductGroupDim1Columns = () => [
    {
        title: <p className="font-xs text-purpleFont">Material Group ID</p>,
        dataIndex: 'material_group_id',
        key: 'material_group_id',
        width: 80,
        sorter: (a: any, b: any) => a.material_group_id - b.material_group_id,
    },
    {
        title: <p className="font-xs text-purpleFont">Material Group</p>,
        dataIndex: 'material_group',
        key: 'material_group',
        sorter: (a: any, b: any) => a.material_group.localeCompare(b.material_group),
    },
    {
        title: <p className="font-xs text-purpleFont">GP PPG</p>,
        dataIndex: 'product_group',
        key: 'product_group',
        sorter: (a: any, b: any) => (a.product_group ?? '').localeCompare(b.product_group ?? ''),
    },
];

export const getCustomProductGroupDim1SelectedColumns = () => [
    {
        title: <p className="font-xs text-purpleFont">Material Group ID</p>,
        dataIndex: 'material_group_id',
        key: 'material_group_id',
        width: 80,
    },
    {
        title: <p className="font-xs text-purpleFont">Material Group</p>,
        dataIndex: 'material_group',
        key: 'material_group',
        ellipsis: true,
    },
];

export type BulkExportReportColumn = {
    label: string;
    key: string;
    sortKey?: string | null;
};

export type BulkExportReportRow = {
    id?: string | number;
    status?: BulkExportReportStatus | string;
    error_message?: string | null;
    updated_at?: number | null;
    [key: string]: any;
};

type BulkExportReportSortObj = {
    sortBy?: string;
    sortOrder?: 'ascend' | 'descend' | null;
};

type BulkExportRowActionHandlers = {
    onRefresh?: (row: BulkExportReportRow) => void;
    onEdit?: (row: BulkExportReportRow) => void;
    onReportDetails?: (row: BulkExportReportRow) => void;
    onDelete?: (row: BulkExportReportRow) => void;
    /** True while the per-row POST /refresh is in flight; disables the icon to
     *  prevent double-clicks during the brief gap before the row enters pending. */
    isRefreshing?: (row: BulkExportReportRow) => boolean;
};

const BulkExportRowActionsPopover = ({
    rowData,
    onEdit,
    onReportDetails,
    onDelete,
    disabled,
}: {
    rowData: BulkExportReportRow;
    onEdit?: (row: BulkExportReportRow) => void;
    onReportDetails?: (row: BulkExportReportRow) => void;
    onDelete?: (row: BulkExportReportRow) => void;
    disabled?: boolean;
}) => {
    const [open, setOpen] = useState(false);

    if (disabled) {
        return (
            <Tooltip
                title="Available once the report is ready."
                placement="top"
                destroyTooltipOnHide
                overlayInnerStyle={{ fontSize: 11 }}
            >
                <span
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 cursor-not-allowed flex items-center justify-center text-gray-300"
                >
                    <MoreOutlined style={{ fontSize: 16 }} />
                </span>
            </Tooltip>
        );
    }

    return (
        <Popover
            open={open}
            onOpenChange={setOpen}
            trigger="click"
            showArrow={false}
            placement="bottomRight"
            overlayInnerStyle={{ borderRadius: 6, padding: '4px 0', minWidth: 150 }}
            content={
                <div>
                    {onEdit && (
                        <div
                            onClick={() => {
                                setOpen(false);
                                onEdit(rowData);
                            }}
                            className="flex items-center gap-2 cursor-pointer px-3 py-2 hover:bg-gray-100"
                        >
                            <EditOutlined />
                            <span className="font-xs">Edit</span>
                        </div>
                    )}
                    {onReportDetails && (
                        <div
                            onClick={() => {
                                setOpen(false);
                                onReportDetails(rowData);
                            }}
                            className="flex items-center gap-2 cursor-pointer px-3 py-2 hover:bg-gray-100"
                        >
                            <InfoCircleOutlined />
                            <span className="font-xs">Report Details</span>
                        </div>
                    )}
                    {onDelete && (
                        <div
                            onClick={() => {
                                setOpen(false);
                                onDelete(rowData);
                            }}
                            className="flex items-center gap-2 cursor-pointer px-3 py-2 hover:bg-gray-100 text-redAlt"
                        >
                            <DeleteOutlined style={{ position: 'relative', top: -1 }} />
                            <span className="font-xs">Delete</span>
                        </div>
                    )}
                </div>
            }
        >
            <span onClick={(e) => e.stopPropagation()} className="p-2 cursor-pointer flex items-center justify-center">
                <MoreOutlined style={{ fontSize: 16 }} />
            </span>
        </Popover>
    );
};

const bulkExportCellFormatters: Record<string, (val: any) => React.ReactNode> = {
    report_type: (val) => {
        const s = val ? String(val) : '';
        return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : null;
    },
    updated_at: (val) => (val == null ? null : dayjs.unix(val).fromNow()),
};

export const getBulkExportReportsTableColumns = (
    headers: BulkExportReportColumn[],
    handlers: BulkExportRowActionHandlers,
    sortObj: BulkExportReportSortObj,
) => {
    return (headers ?? []).map((header) => {
        const sortKey = header?.sortKey ?? undefined;

        // Status icon column
        if (header?.key === 'status') {
            return {
                title: '',
                dataIndex: header?.key,
                key: header?.key,
                width: 36,
                fixed: 'left' as const,
                onCell: () => ({ style: { padding: '0 0 0 8px' } }),
                render: (val: BulkExportReportRow['status'], rowData: BulkExportReportRow) => {
                    if (PENDING_BULK_EXPORT_STATUSES.has(String(val))) {
                        return (
                            <Spin
                                indicator={
                                    <LoadingOutlined style={{ fontSize: 14, color: colorConstants.violet }} spin />
                                }
                            />
                        );
                    }
                    if (val === BulkExportReportStatus.SUCCESS) {
                        return <CheckCircleFilled style={{ color: colorConstants.limeGreen, fontSize: 16 }} />;
                    }
                    if (val === BulkExportReportStatus.ERROR) {
                        return (
                            <Tooltip
                                title={rowData?.error_message ?? 'Error'}
                                placement="right"
                                destroyTooltipOnHide
                                overlayInnerStyle={{ fontSize: 11 }}
                            >
                                <CloseCircleFilled style={{ color: colorConstants.redAlt, fontSize: 16 }} />
                            </Tooltip>
                        );
                    }
                    return null;
                },
            };
        }

        // Quick Actions column — labeled `Refresh data` + `Export` cell actions.
        // Refresh is disabled while the row is queued / in_progress.
        if (header?.key === 'quick_actions') {
            return {
                title: <p className="font-xs text-purpleFont m-0">{header?.label}</p>,
                dataIndex: header?.key,
                key: header?.key,
                width: 220,
                render: (_columnData: any, rowData: BulkExportReportRow) => {
                    const isPending = PENDING_BULK_EXPORT_STATUSES.has(String(rowData?.status));
                    const isErrored = String(rowData?.status) === BulkExportReportStatus.ERROR;
                    const isRowRefreshing = handlers.isRefreshing?.(rowData) ?? false;
                    const canRefresh = !isPending && !isRowRefreshing && !isErrored;
                    const validityDays = TPO_UI_LIMITS.BULK_EXPORT_DOWNLOAD_VALIDITY_DAYS;
                    const isExpired =
                        rowData?.updated_at != null &&
                        dayjs().diff(dayjs.unix(rowData.updated_at as number), 'day') > validityDays;
                    const canExport = rowData?.status === BulkExportReportStatus.SUCCESS && !isExpired;
                    const pendingTooltip = 'Available once the report is ready.';
                    const expiredTooltip = TpoMessages.info.BULK_EXPORT_DOWNLOAD_EXPIRED(validityDays);
                    const errorTooltip = 'This report failed.';
                    const refreshTooltip = isRowRefreshing
                        ? 'Refreshing...'
                        : isErrored
                        ? errorTooltip
                        : canRefresh
                        ? ''
                        : pendingTooltip;
                    const exportDisabledTooltip = isRowRefreshing
                        ? 'Refreshing...'
                        : isErrored
                        ? errorTooltip
                        : isExpired
                        ? expiredTooltip
                        : pendingTooltip;
                    return (
                        <div className="flex items-center gap-4 font-xs">
                            {handlers.onRefresh && (
                                <Tooltip
                                    title={refreshTooltip}
                                    placement="top"
                                    destroyTooltipOnHide
                                    overlayInnerStyle={{ fontSize: 11 }}
                                >
                                    <span
                                        onClick={() => {
                                            if (!canRefresh) return;
                                            handlers.onRefresh?.(rowData);
                                        }}
                                        className={`flex items-center gap-1 ${
                                            canRefresh
                                                ? 'cursor-pointer text-violetAlt'
                                                : 'cursor-not-allowed text-gray-300'
                                        }`}
                                    >
                                        <SyncOutlined style={{ fontSize: 14 }} spin={isPending || isRowRefreshing} />
                                        <span>Refresh data</span>
                                    </span>
                                </Tooltip>
                            )}
                            <DownloadFileButton
                                url={
                                    rowData?.id != null
                                        ? tradePromoOptimizationUrls.getReportDownloadUrl(rowData.id)
                                        : ''
                                }
                                fileName={`${rowData?.name || `report_${rowData?.id ?? ''}`}.xlsx`}
                                label="Export"
                                disabled={!canExport || isRowRefreshing}
                                disabledTooltip={exportDisabledTooltip}
                                infoMessage="Your report download is starting..."
                                successMessage="Download started."
                                errorMessage={`Failed to download "${rowData?.name ?? ''}". Please try again.`}
                            />
                        </div>
                    );
                },
            };
        }

        // popover column
        if (header?.key === 'edit') {
            return {
                title: '',
                dataIndex: header?.key,
                key: header?.key,
                width: 48,
                fixed: 'right' as const,
                render: (_columnData: any, rowData: BulkExportReportRow) => {
                    const isRowRefreshing = handlers.isRefreshing?.(rowData) ?? false;
                    return (
                        <BulkExportRowActionsPopover
                            rowData={rowData}
                            onEdit={handlers.onEdit}
                            onReportDetails={handlers.onReportDetails}
                            onDelete={handlers.onDelete}
                            disabled={PENDING_BULK_EXPORT_STATUSES.has(String(rowData?.status)) || isRowRefreshing}
                        />
                    );
                },
            };
        }

        // Default data column — plain text, with optional per-key value formatting

        const formatter = bulkExportCellFormatters[header?.key];
        return {
            title: <p className="font-xs text-purpleFont m-0">{header?.label}</p>,
            dataIndex: header?.key,
            key: header?.key,
            sortKey,
            sortOrder: sortKey && sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null,
            sorter: sortKey ? true : false,
            sortDirections: ['ascend', 'descend', 'ascend'] as ('ascend' | 'descend')[],
            width: 200,
            render: (columnData: any) => {
                const display = formatter ? formatter(columnData) : columnData;
                return (
                    <div className="font-xs">
                        {display === null || display === undefined || display === '' ? '-' : display}
                    </div>
                );
            },
        };
    });
};
